import { memo } from 'react';
import styled from 'styled-components';
import { Loader } from './loader';
import { ListViewItem } from './item';
import { IListViewContentManagerProps, IItemData } from './types';
import { useItemsViewManager, useSelectionInfo } from './hooks';

const Placeholder = styled.div`
    display: block;
    position: absolute;
    text-align: center;
`;

const ViewPort = styled.div<{ $isContentReadyToView: boolean }>`
    opacity: ${({ $isContentReadyToView }) => ($isContentReadyToView ? 1 : 0)};
`;

export const ContentManager = memo((props: IListViewContentManagerProps) => {
    const {
        activeItemKey, getKey, onRenderItemContent, onItemSelect, onRenderItem,
        isHorizontallyAligned, hideLoader, autoResizeThreshold
    } = props;

    const { isContentReadyToView, itemsInView, placeholderPosition, onItemRectCapture, getOccupiedSpace } = useItemsViewManager(props);

    const { isScrollToSelectionPending, isSelected, getItemSelectableConfig } = useSelectionInfo({ ...props, itemsInView, isContentReadyToView, getOccupiedSpace });

    const renderItem = (itemData: IItemData) => {
        const { item, position: { height, left, top, width } } = itemData;
        const key = getKey(item);
        return (
            <ListViewItem
                key={key}
                isSelected={isSelected(item)}
                data={item}
                onRenderItemContent={onRenderItemContent}
                onRenderItem={onRenderItem}
                onSelect={onItemSelect}
                top={top}
                left={left}
                height={height}
                width={width}
                isHorizontallyAligned={isHorizontallyAligned}
                autoResizeThreshold={autoResizeThreshold}
                selectable={getItemSelectableConfig(item)}
                isFocused={key === activeItemKey}
                onItemRectCapture={onItemRectCapture} />
        );
    };

    return (
        <>
            <Loader hidden={hideLoader || (isContentReadyToView && !isScrollToSelectionPending)} />
            <ViewPort $isContentReadyToView={(isContentReadyToView && !isScrollToSelectionPending)}>
                {itemsInView.map(renderItem)}
                {placeholderPosition !== undefined ? <Placeholder key="bottomPlaceholder" style={placeholderPosition} /> : null}
            </ViewPort>
        </>
    );
});

ContentManager.displayName = 'ContentManager';
